import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Slide,
  Zoom,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  MonetizationOn as CoinIcon,
  ShoppingCart as ShoppingCartIcon,
  PlayArrow as PlayIcon,
  Tv as AdIcon,
  Warning as WarningIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import PurchaseCoins from "./PurchaseCoins";
import PurchaseCoinsAirWAallex from "./PurchaseCoinsAirWallex";
import PurchaseCoinsFacebook from "./PurchaseCoinsFacebook";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useUser } from "../authMethods/UserContext";

const stripePromise = loadStripe("your-stripe-public-key");

// Animated Coin Component
const AnimatedCoin = ({ size = 40, delay = 0 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: "linear-gradient(145deg, #FFD700, #FFA500)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 4px 20px rgba(255, 215, 0, 0.4)",
      animation: `coinBounce 2s ease-in-out infinite ${delay}s`,
      "@keyframes coinBounce": {
        "0%, 100%": { transform: "translateY(0) rotateY(0deg)" },
        "25%": { transform: "translateY(-10px) rotateY(90deg)" },
        "50%": { transform: "translateY(-5px) rotateY(180deg)" },
        "75%": { transform: "translateY(-10px) rotateY(270deg)" },
      },
    }}
  >
    <CoinIcon sx={{ color: "white", fontSize: size * 0.6 }} />
  </Box>
);

// Coins Progress Bar Component
const CoinsProgressBar = ({ needed, current }) => {
  const progress = (current / needed) * 100;
  
  
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        {/* <Typography variant="body2" color="text.secondary">
          Progress to Goal
        </Typography> */}
        <Typography variant="body2" color="text.secondary">
          {current} / {needed}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(progress, 100)}
        sx={{
          height: 8,
          borderRadius: 5,
          backgroundColor: "rgba(255, 215, 0, 0.2)",
          "& .MuiLinearProgress-bar": {
            background: "linear-gradient(90deg, #FFD700, #FFA500)",
            borderRadius: 5,
          },
        }}
      />
      {/* <Typography
        variant="caption"
        sx={{ 
          color: remaining > 0 ? "error.main" : "success.main",
          fontWeight: "bold",
          display: "block",
          textAlign: "center",
          mt: 1
        }}
      >
        {remaining > 0 ? `${remaining} coins needed` : "Goal reached!"}
      </Typography> */}
    </Box>
  );
};

// Interactive Action Button Component
const InteractiveActionButton = ({ 
  children, 
  onClick, 
  variant = "primary", 
  fullWidth = false,
  startIcon,
  disabled = false 
}) => {
  const [isPressed, setIsPressed] = useState(false);
  
  const getVariantStyles = () => {
    switch (variant) {
      case "buy":
        return {
          background: "linear-gradient(135deg, #4CAF50, #45a049)",
          hoverBackground: "linear-gradient(135deg, #45a049, #3e8e41)",
          color: "white",
          boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
        };
      case "ad":
        return {
          background: "linear-gradient(135deg, #FF9800, #F57C00)",
          hoverBackground: "linear-gradient(135deg, #F57C00, #E65100)",
          color: "white",
          boxShadow: "0 4px 20px rgba(255, 152, 0, 0.3)",
        };
      case "cancel":
        return {
          background: "linear-gradient(135deg, #f44336, #d32f2f)",
          hoverBackground: "linear-gradient(135deg, #d32f2f, #c62828)",
          color: "white",
          boxShadow: "0 4px 20px rgba(244, 67, 54, 0.3)",
        };
      default:
        return {
          background: "linear-gradient(135deg, #2196F3, #1976D2)",
          hoverBackground: "linear-gradient(135deg, #1976D2, #1565C0)",
          color: "white",
          boxShadow: "0 4px 20px rgba(33, 150, 243, 0.3)",
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <Button
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      fullWidth={fullWidth}
      disabled={disabled}
      startIcon={startIcon}
      sx={{
        background: styles.background,
        color: styles.color,
        fontWeight: 600,
        fontSize: "1rem",
        padding: "12px 24px",
        borderRadius: "12px",
        textTransform: "none",
        boxShadow: styles.boxShadow,
        transform: isPressed ? "scale(0.95)" : "scale(1)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          background: styles.hoverBackground,
          transform: "scale(1.05)",
          boxShadow: styles.boxShadow.replace("0.3", "0.5"),
        },
        "&:disabled": {
          background: "rgba(0, 0, 0, 0.12)",
          color: "rgba(0, 0, 0, 0.26)",
          transform: "scale(1)",
        },
      }}
    >
      {children}
    </Button>
  );
};

const NotEnoughCoinsDialog = ({
  open,
  onClose,
  onWatchAd,
  neededCoins,
  actualCoins,
  buy = false,
  scrollToCoins = false
}) => {
  const { t } = useTranslation();
  const { userData } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [showPurchaseOptions, setShowPurchaseOptions] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (buy) {
      setShowPurchaseOptions(true);
    } else {
      setShowPurchaseOptions(false);
    }
  }, [buy, t]);

  useEffect(() => {
    if (open) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 500);
      return () => clearTimeout(timer);
    }
    if (!open) {
      setShowPurchaseOptions(buy);
    }
  }, [open, buy]);

  const handleBuyCoinsClick = () => {
    setShowPurchaseOptions(true);
  };

  const handleClose = () => {
    setShowPurchaseOptions(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxWidth: isMobile ? "100%" : isTablet ? "90vw" : "800px",
          minWidth: isMobile ? "100%" : "400px",
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? "100%" : buy || showPurchaseOptions ? "600px" : "400px",
          maxHeight: isMobile ? "100%" : "90vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          overflow: "hidden",
        },
      }}
      sx={{ zIndex: 3500 }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #FFD700, #FFA500)",
          color: "white",
          textAlign: "center",
          fontWeight: "bold",
          fontSize: { xs: "1.3rem", sm: "1.6rem" },
          py: 2,
          position: "relative",
          boxShadow: "0 4px 20px rgba(255, 215, 0, 0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          <AnimatedCoin size={32} />
          <Typography variant="h5" component="span" sx={{ fontWeight: "bold" }}>
            {buy ? t("Buy Coins") : t("Not Enough Coins")}
          </Typography>
          <AnimatedCoin size={32} delay={0.5} />
        </Box>
        
        {!isMobile && (
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
                transform: "scale(1.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "transparent" }}>
        {!buy && (
          <Zoom in={!isAnimating} timeout={500}>
            <Card
              elevation={0}
              sx={{
                mb: 3,
                background: "linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))",
                backdropFilter: "blur(10px)",
                borderRadius: 3,
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: "warning.main" }}>
                    <WarningIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
                    {t("Insufficient Coins")}
                  </Typography>
                </Box>
                
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    color: "text.secondary",
                    lineHeight: 1.6,
                  }}
                >
                  {t("You don't have enough coins to play the selected game")}
                </Typography>

                <CoinsProgressBar needed={neededCoins} current={actualCoins} />

                <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                  <Chip
                    icon={<CoinIcon />}
                    label={`${t("Needed")}: ${neededCoins}`}
                    color="error"
                    variant="outlined"
                    sx={{ fontWeight: "bold" }}
                  />
                  <Chip
                    icon={<CoinIcon />}
                    label={`${t("You Have")}: ${actualCoins}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: "bold" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        )}

        {!showPurchaseOptions ? (
          <Zoom in={!isAnimating} timeout={700}>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  textAlign: "center",
                  mb: 3,
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                {t("Choose an option to continue")}
              </Typography>
              
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <InteractiveActionButton
                  onClick={handleBuyCoinsClick}
                  variant="buy"
                  fullWidth={isMobile}
                  startIcon={<ShoppingCartIcon />}
                >
                  {t("Buy Coins")}
                </InteractiveActionButton>

                {!buy && (
                  <InteractiveActionButton
                    onClick={userData?.name?.includes("_Guest") ? () => {
                      // Handle "How to get more coins" action
                      console.log("Show how to get more coins info");
                      // You can add navigation to help page or show info dialog
                    } : onWatchAd}
                    variant="ad"
                    fullWidth={isMobile}
                    startIcon={userData?.name?.includes("_Guest") ? <StarIcon /> : <AdIcon />}
                  >
                    {userData?.name?.includes("_Guest") ? t("How to get more coins") : t("Watch Ad")}
                  </InteractiveActionButton>
                )}
              </Box>
            </Box>
          </Zoom>
        ) : (
          <Slide direction="up" in={showPurchaseOptions} timeout={500}>
            <Box>
              {window.FB_USER_DATA ? (
                <PurchaseCoinsFacebook
                  onClose={handleClose}
                  userData={userData}
                />
              ) : (
                <Elements stripe={stripePromise}>
                  <PurchaseCoinsAirWAallex
                    onClose={handleClose}
                    enablePurchase={true}
                    userData={userData}
                    scrollToCoins={scrollToCoins}
                  />
                </Elements>
              )}
            </Box>
          </Slide>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions 
        sx={{ 
          justifyContent: "space-between",
          p: { xs: 2, sm: 3 },
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)",
        }}
      >
        {showPurchaseOptions && (
          <Button
            onClick={() => setShowPurchaseOptions(false)}
            startIcon={<PlayIcon sx={{ transform: "rotate(180deg)" }} />}
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              textTransform: "none",
            }}
          >
            {t("Back")}
          </Button>
        )}
        
        <Box sx={{ flexGrow: 1 }} />
        
        <InteractiveActionButton
          onClick={handleClose}
          variant="cancel"
          startIcon={<CloseIcon />}
        >
          {t("Cancel")}
        </InteractiveActionButton>
      </DialogActions>

      {/* Decorative Elements */}
      <Box
        sx={{
          position: "absolute",
          top: -20,
          left: -20,
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 165, 0, 0.1))",
          zIndex: -1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -30,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(25, 118, 210, 0.1))",
          zIndex: -1,
        }}
      />
    </Dialog>
  );
};

export default NotEnoughCoinsDialog;